import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
	ExclamationCircleIcon,
	DocumentTextIcon,
	UserIcon,
	DocumentDuplicateIcon,
	DocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { PLAGIARISM_SCORE_THRESHOLDS } from "../../services/plagiarism";

interface PlagiarismDescriptionProps {
	value: number; // 0-1
}

const getPlagiarismScoreDescription = (
	score: number,
): {
	title: string;
	Icon: React.ElementType;
	description: string;
} => {
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.veryLikelyPlagiarism)
		return {
			title: "Severe Plagiarism Detected",
			Icon: ExclamationCircleIcon,
			description:
				"This document contains extensive plagiarism. Large portions closely match existing sources, indicating minimal originality.",
		};
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mostlyPlagiarism)
		return {
			title: "High Plagiarism Detected",
			Icon: DocumentDuplicateIcon,
			description:
				"This document shows significant overlap with existing sources. Multiple sections appear copied or insufficiently paraphrased.",
		};
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mixed)
		return {
			title: "Moderate Plagiarism Detected",
			Icon: DocumentTextIcon,
			description:
				"This document contains noticeable overlap with existing works. Some passages are similar to sources, while others are original.",
		};
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mostlyOriginal)
		return {
			title: "Low Plagiarism Detected",
			Icon: DocumentCheckIcon,
			description:
				"This document shows minor overlap with existing sources. Most of the text appears original, with only limited reuse of ideas or phrases.",
		};
	if (score < 0.01) {
		return {
			title: "No Plagiarism Detected",
			Icon: UserIcon,
			description:
				"This document shows no signs of plagiarism. The content appears fully original with no similarity to existing sources.",
		};
	}
	return {
		title: "Minimal Plagiarism Detected",
		Icon: UserIcon,
		description:
			"This document shows very limited similarity to existing sources. The content is largely original with only minor overlaps.",
	};
};

export function PlagiarismDescription({ value }: PlagiarismDescriptionProps) {
	const { title, description, Icon } = getPlagiarismScoreDescription(value);

	return (
		<Alert variant="default">
			<Icon />
			<AlertTitle>{title}</AlertTitle>
			<AlertDescription>
				<p>{description}</p>
			</AlertDescription>
		</Alert>
	);
}
